import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './Tarot.css';

const API_PREFIX = process.env.REACT_APP_API_PREFIX || '/api';

const OrientationExplanation = ({ orientation }) => {
  if (orientation === 'upright') {
    return (
      <div className="orientation-explanation">
        <p className="orientation-meaning">
          <strong>Oikeinpäin</strong> = kortin pääviesti ilmenee suoraan ja ulkoisesti elämässäsi. 
          Energia virtaa vapaasti ja teema näkyy selkeästi tilanteessasi.
        </p>
        <p className="orientation-question">
          💡 Kysymys: Miten tämä teema näkyy juuri nyt elämässäni?
        </p>
      </div>
    );
  }
  return (
    <div className="orientation-explanation">
      <p className="orientation-meaning">
        <strong>Käänteinen</strong> = sama teema, mutta sisäisenä prosessina. Voi viitata esteeseen, 
        viivästykseen, varjopuoleen tai kutsuu sinua tarkastelemaan aihetta syvemmin.
      </p>
      <p className="orientation-question">
        💡 Kysymys: Mikä sisäinen este tai oppi liittyy tähän teemaan?
      </p>
    </div>
  );
};

const Tarot = () => {
  const [dailyCard, setDailyCard] = useState(null);
  const [drawnCard, setDrawnCard] = useState(null);
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(true);
  const [drawing, setDrawing] = useState(false);
  const [error, setError] = useState(null);
  const drawnCardRef = useRef(null);

  useEffect(() => {
    fetchDailyCard();
  }, []);

  const fetchDailyCard = async () => {
    setError(null);
    try {
      const response = await axios.get(`${API_PREFIX}/tarot/daily`);
      setDailyCard(response.data);
    } catch (err) {
      console.error('Failed to fetch daily card:', err);
      setError('Päivän kortin lataus epäonnistui. Yritä myöhemmin uudelleen.');
    } finally {
      setLoading(false);
    }
  };

  const handleDrawCard = async (e) => {
    e.preventDefault();
    const trimmedQuestion = question ? String(question).trim() : '';
    if (!trimmedQuestion) {
      setError('Kirjoita ensin kysymyksesi');
      return;
    }

    setError(null);
    setDrawing(true);
    try {
      const response = await axios.post(`${API_PREFIX}/tarot/draw`, {
        question: trimmedQuestion,
      });
      setDrawnCard(response.data);
      setQuestion('');
      setTimeout(() => {
        drawnCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } catch (err) {
      console.error('Failed to draw card:', err);
      setError('Kortin nosto epäonnistui. Yritä uudelleen.');
    } finally {
      setDrawing(false);
    }
  };

  const safeKeywords = (keywords) => {
    if (!keywords || !Array.isArray(keywords)) return '';
    return keywords.join(', ');
  };

  const safeDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      return isNaN(d.getTime()) ? '' : d.toLocaleDateString('fi-FI');
    } catch {
      return '';
    }
  };

  return (
    <div className="tarot">
      <div className="container">
        <div className="tarot-header">
          <h1>Tarot</h1>
          <p className="tarot-subtitle">
            Kokeile tarotin taikuutta ja löydä ohjeita elämäsi kysymyksiin
          </p>
        </div>

        {error && (
          <div className="tarot-alert" role="alert">
            {error}
          </div>
        )}

        {loading ? (
          <div className="tarot-loading">
            <div className="moon-loader">
              <div className="moon"></div>
              <p>Ladataan päivän korttia...</p>
            </div>
          </div>
        ) : (
          <>
            {dailyCard && dailyCard.card && (
              <section className="daily-card-section">
                <h2 className="section-title">Päivän Tarot-kortti</h2>
                <div className="tarot-card-display card">
                  {dailyCard.card.imageUrl && (
                    <div className="card-image-container">
                      <img
                        src={dailyCard.card.imageUrl}
                        alt={dailyCard.card.nameFi || dailyCard.card.name || 'Tarot-kortti'}
                        className={`tarot-card-image ${dailyCard.card.orientation === 'reversed' ? 'reversed' : ''}`}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    </div>
                  )}
                  <div className="card-header">
                    {dailyCard.card.number != null && (
                      <div className="card-number">{dailyCard.card.number}</div>
                    )}
                    <h3 className="card-name">{dailyCard.card.nameFi || dailyCard.card.name || ''}</h3>
                    {dailyCard.card.orientation && (
                      <>
                        <div className="card-orientation">
                          {dailyCard.card.orientation === 'upright' ? 'Oikeinpäin' : 'Käänteinen'}
                        </div>
                        <OrientationExplanation orientation={dailyCard.card.orientation} />
                      </>
                    )}
                  </div>
                  {dailyCard.interpretation && (
                    <div className="card-interpretation">
                      {dailyCard.interpretation.description && (
                        <p className="card-meaning">{dailyCard.interpretation.description}</p>
                      )}
                      {dailyCard.interpretation.keywords && (
                        <div className="card-keywords">
                          <strong>Avainsanat:</strong> {safeKeywords(dailyCard.interpretation.keywords)}
                        </div>
                      )}
                    </div>
                  )}
                  {dailyCard.date && (
                    <div className="card-date">
                      Päivämäärä: {safeDate(dailyCard.date)}
                    </div>
                  )}
                </div>
              </section>
            )}

            <section className="draw-card-section">
              <h2 className="section-title">Nosta oma kortti</h2>
              <div className="tarot-question-card card">
                <p className="question-instruction">
                  Kirjoita kysymyksesi alle, ja nostamme sinulle yhden tarot-kortin
                  henkilökohtaisella tulkinnalla.
                </p>
                <form onSubmit={handleDrawCard} className="tarot-question-form">
                  <textarea
                    className="question-input"
                    placeholder="Kirjoita kysymyksesi tähän... (esim. Mitä minun pitäisi tietää tänään?)"
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    rows="4"
                    required
                  />
                  <button
                    type="submit"
                    className="btn btn-primary draw-card-btn"
                    disabled={drawing}
                  >
                    {drawing ? 'Nostetaan korttia...' : 'Nosta kortti →'}
                  </button>
                </form>
              </div>

              {drawnCard && drawnCard.card && (
                <div ref={drawnCardRef} className="drawn-card-display card">
                  {drawnCard.card.imageUrl && (
                    <div className="card-image-container">
                      <img
                        src={drawnCard.card.imageUrl}
                        alt={drawnCard.card.nameFi || drawnCard.card.name || 'Tarot-kortti'}
                        className={`tarot-card-image ${drawnCard.card.orientation === 'reversed' ? 'reversed' : ''}`}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    </div>
                  )}
                  <div className="card-header">
                    {drawnCard.card.number != null && (
                      <div className="card-number">{drawnCard.card.number}</div>
                    )}
                    <h3 className="card-name">{drawnCard.card.nameFi || drawnCard.card.name || ''}</h3>
                    {drawnCard.card.orientation && (
                      <>
                        <div className="card-orientation">
                          {drawnCard.card.orientation === 'upright' ? 'Oikeinpäin' : 'Käänteinen'}
                        </div>
                        <OrientationExplanation orientation={drawnCard.card.orientation} />
                      </>
                    )}
                  </div>
                  {drawnCard.question && (
                    <div className="card-question">
                      <strong>Kysymyksesi:</strong> &quot;{drawnCard.question}&quot;
                    </div>
                  )}
                  {drawnCard.interpretation && (
                    <div className="card-interpretation">
                      {drawnCard.interpretation.description && (
                        <p className="card-meaning">{drawnCard.interpretation.description}</p>
                      )}
                      {drawnCard.interpretation.keywords && (
                        <div className="card-keywords">
                          <strong>Avainsanat:</strong> {safeKeywords(drawnCard.interpretation.keywords)}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
};

export default Tarot;
